import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import DataTable from '../../components/ui/DataTable';
import { useAuth } from '../../context/AuthContext';
import SmartStatsRow from '../dashboard/cards/SmartStatsRow';
import { resolveHasHaModule } from '../dashboard/resolveHasHaModule';
import { useHrAnalyticsApi } from './config';

// Task 16 (Plan 1) — İK Analitiği Genel Bakış sayfası. Veri Hazırlık Skoru (kart) +
// Metrik Kataloğu (DataTable), yapı kasıtlı olarak sade tutulmuştu.
//
// Task 16 (Plan 2) — spec §13.3 zenginleştirmesi: üst başlık (hrAnalytics.title, beş
// dilde tanımlıydı ama hiç render edilmiyordu — takip listesi madde 9) + akıllı kart
// özeti (Görev 12'nin SmartStatCard'ı, Görev 13'ün SmartStatsRow entegrasyonu ÜZERİNDEN
// — yeniden İMPLEMENTE EDİLMEDİ, aynı 4 metrik/aynı fetch/aynı GridItem deseni tekrar
// kullanıldı) + ReadinessCard'a yükleme göstergesi (madde 11). hasHaModule burada da
// DashboardPage'in AYNI ilkeliyle (resolveHasHaModule — hasModule('HA') && canRead('HA'))
// hesaplanır; bu sayfaya zaten yalnız HA'sı olan bir tenant'tan erişilebilir (route +
// menü modül-kapılı), ama ikinci bir "modül var mı" yolu İCAT ETMEMEK için tek kaynağa
// bağlı kalındı — DashboardPage/SmartStatsRow'daki AYNI değer, AYNI fonksiyon.
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

// Sütun sayısı KART SAYISINDAN türetilir, elle atanmaz. Eskiden burası da 12 sütunluk sabit
// bir ızgaraydı ve içindeki kartlar `span 3` taşıyordu: dört metrikten biri kilitliyse
// (insufficient_history vb.) satırın çeyreği boş kalıyordu. Ana paneldeki aynı hatanın bu
// sayfadaki kopyasıydı.
const SmartStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ReadinessCard = styled.div`
  background: ${({ theme }) => theme.colors.surface.card};
  border-radius: ${({ theme }) => theme.metrics.radius.large};
  box-shadow: 0 1px 4px rgba(${({ theme }) => theme.colors.shadow.rgb}, 0.06);
  padding: 20px 24px;
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.9s linear infinite;
  color: ${({ theme }) => theme.colors.primary.main};
`;

const ReadinessTitle = styled.h2`
  margin: 0 0 16px 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ScoreRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 16px;
`;

const ScoreValue = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 40px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.extraBold};
  color: ${({ theme }) => theme.colors.primary.main};
`;

const ScoreMax = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.surface.border};

  &:last-child { border-bottom: none; }
`;

const ActionLabel = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ActionCount = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.muted};
  flex-shrink: 0;
`;

const EmptyActions = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.muted};
  padding: 4px 0;
`;

// Ton → tema rengi eşlemesi: positive/locked auxiliar.success/danger'a bağlanır (FunnelPlot.js
// ve performance-tasks/config/index.js'teki AYNI $tone→theme.colors.auxiliar deseni — hardcode
// hex YOK, frontend/CLAUDE.md). Arkaplan aynı rengin "22" alfa-karıştırılmış hâli (IdpFlowBar.js/
// OffboardingDetailsPage.js'teki AYNI süfiks) — nötr ton için text.muted kullanılır (bu depoda
// ayrı bir "nötr gri" token'ı yok).
const StateBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ $tone, theme }) =>
    `${$tone === 'positive' ? theme.colors.auxiliar.success :
      $tone === 'locked' ? theme.colors.auxiliar.danger :
      theme.colors.text.muted}22`};
  color: ${({ $tone, theme }) =>
    $tone === 'positive' ? theme.colors.auxiliar.success :
    $tone === 'locked' ? theme.colors.auxiliar.danger :
    theme.colors.text.muted};
`;

// state → rozet tonu: available yeşil, locked_module kırmızı/kilit, diğer üç
// durum (disabled_by_settings/insufficient_history/insufficient_data) nötr gri.
//
// export: saf ve dallı bir eşleme, yani doğrudan birim testlenebilir olmalı
// (orgChartModel.js'teki buildPositionPayload için verilen AYNI gerekçe — saf fonksiyon
// render olmadan birim testlenir).
export function stateTone(state) {
  if (state === 'available') return 'positive';
  if (state === 'locked_module') return 'locked';
  return 'neutral';
}

// Katalog satırının "gerekçe" hücresinin metni. Üç dal var ve üçü de farklı bir soruyu
// cevaplar:
//   • available      → gerekçe YOK, TEMEL (hangi modüllerin verisiyle hesaplandı) gösterilir.
//   • locked_module + missingGroups → gerekçe + EKSİK MODÜL KÜMELERİ. Gruplar CNF'tir:
//     grup İÇİ "/" (VEYA), gruplar ARASI "+" (VE) — "SM / TA + LM" = (SM veya TA) ve LM.
//     Ayraçları karıştırmak kullanıcıya yanlış bir abonelik listesi okutur.
//   • diğer kilitli durumlar → yalnız gerekçe metni.
//
// export: saf (t dışında bağımlılığı yok) ve üç dallı — render testinden geçmeden
// doğrudan sınanabilmeli.
export function buildReasonText(row, t) {
  if (row.state === 'available') {
    return `${t('hrAnalytics.basis')}: ${(row.basis || []).join(', ')}`;
  }
  const reasonText = t(`responseErrors.${row.reasonKey}`);
  if (row.state === 'locked_module' && row.missingGroups?.length) {
    const missing = row.missingGroups.map((g) => g.join(' / ')).join(' + ');
    return `${reasonText} (${missing})`;
  }
  return reasonText;
}

export default function HrAnalyticsOverviewPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const { hasModule, canRead } = useAuth();
  const { getCatalog, getReadiness } = useHrAnalyticsApi();

  const hasHaModule = resolveHasHaModule({ hasModule, canRead });

  const [catalog, setCatalog] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    // Hata toast'ı YOK — hatalar yalnız Axios interceptor'da ele alınır (CLAUDE.md).
    setFetching(true);
    Promise.all([
      getCatalog().then((r) => setCatalog(r?.data?.data ?? [])),
      getReadiness().then((r) => setReadiness(r?.data?.data ?? null)),
    ]).finally(() => setFetching(false));
  }, [getCatalog, getReadiness]);

  // Çevrilmiş ad satırın kendi alanı olur — tablo bileşeni sütunları ada göre eşlediği için
  // iki sütunun aynı `name`'i taşıması mümkün değil. Çevirisi olmayan kod OLDUĞU GİBİ kalır:
  // yanlış bir ad uydurmaktan iyidir ve eksikliği görünür kılar.
  const tableRows = useMemo(
    () => catalog.map((m) => ({ ...m, metricName: t(`hrAnalytics.metricNames.${m.code}`, m.code) })),
    [catalog, t]
  );

  // Metrik gezgini (2026-08-03): "Mevcut" satıra tıklamak detay sayfasını açar — katalog
  // ölü bir envanter listesi değil, gezilebilir bir kütüphane. Kilitli satırda gezilecek
  // veri yok; tıklama sessizce yoksayılır (gerekçe zaten satırda görünür).
  const openMetricDetail = useCallback((row) => {
    if (row?.state === 'available') history.push(`/hr-analytics/metrics/${row.code}`);
  }, [history]);

  const columns = useMemo(() => [
    // Tablo eskiden YALNIZ kodu gösteriyordu (`demographics.age`, `headcount.byEmploymentType`
    // gibi) — kullanıcı bir metriğin ne ölçtüğünü koddan çıkaramaz. Ad sütunu öne alındı; kod
    // teknik referans olarak kaldı (destek/dokümantasyon konuşurken gerekiyor).
    //
    // Sütun adı `metricName` — `code` DEĞİL. İlk denemede iki sütun da `name: 'code'` taşıyordu
    // ve tablo bileşeni sütunları ADA göre eşlediği için mükerrer ad ilkini eziyordu: çeviriler
    // hiç görünmüyordu. Ad artık satırda türetilmiş GERÇEK bir alan (aşağıda `tableRows`), bu
    // sayede sıralama ve filtreleme de çevrilmiş metin üzerinde çalışıyor.
    {
      name: 'metricName',
      label: t('hrAnalytics.columns.name'),
      options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
    {
      name: 'code',
      label: t('hrAnalytics.columns.code'),
      options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
    // Katman/durum filtreleri dropdown'dır (bulk-leaves'in isHourly/isPaid kolonlarındaki AYNI
    // idiom): `names` HAM enum değerlerini taşır (satır verisi ham — client-side eşleşme onunla
    // yapılır), `renderValue`/`customFilterListOptions` kullanıcıya çeviriyi gösterir.
    {
      name: 'tier',
      label: t('hrAnalytics.columns.tier'),
      options: {
        filter: true,
        sort: true,
        filterType: 'dropdown',
        filterOptions: {
          names: ['descriptive', 'diagnostic', 'early_warning', 'predictive'],
          renderValue: (v) => t(`hrAnalytics.tiers.${v}`),
        },
        customFilterListOptions: { render: (v) => t(`hrAnalytics.tiers.${v}`) },
        customBodyRender: (value) => t(`hrAnalytics.tiers.${value}`),
      },
    },
    {
      name: 'state',
      label: t('hrAnalytics.columns.state'),
      options: {
        filter: true,
        sort: true,
        filterType: 'dropdown',
        filterOptions: {
          names: ['available', 'locked_module', 'disabled_by_settings', 'insufficient_history', 'insufficient_data'],
          renderValue: (v) => t(`hrAnalytics.states.${v}`),
        },
        customFilterListOptions: { render: (v) => t(`hrAnalytics.states.${v}`) },
        customBodyRender: (value) => (
          <StateBadge $tone={stateTone(value)}>{t(`hrAnalytics.states.${value}`)}</StateBadge>
        ),
      },
    },
    {
      name: 'reasonKey',
      label: t('hrAnalytics.columns.reason'),
      options: {
        filter: false,
        sort: false,
        // available → hangi modüllerle hesaplandığı (Temel); locked_module → gerekçe +
        // eksik CNF grupları ("SM / TA" OR grupları "+" ile AND edilir, bkz. task-16
        // brief); diğer kilitli durumlar → yalnız gerekçe. Kilitli metrikler ASLA
        // gizlenmez — gerekçesiyle birlikte görünür kalır (bu sayfanın amacı budur).
        customBodyRender: (_, tableMeta) => {
          const row = tableRows[tableMeta.rowIndex];
          if (!row) return null;
          return buildReasonText(row, t);
        },
      },
    },
  ], [t, tableRows]);

  return (
    <Container>
      <ReadinessCard>
        <ReadinessTitle>{t('hrAnalytics.readiness')}</ReadinessTitle>
        {fetching ? (
          <LoadingState data-role="ha-readiness-loading">
            <Spinner size={28} aria-label={t('common.loading')} />
          </LoadingState>
        ) : (
          <>
            <ScoreRow>
              <ScoreValue>{readiness ? readiness.score : '—'}</ScoreValue>
              <ScoreMax>/ 100</ScoreMax>
            </ScoreRow>
            {readiness?.actions?.length > 0 ? (
              readiness.actions.map((a) => (
                <ActionRow key={a.reasonKey}>
                  <ActionLabel>{t(`responseErrors.${a.reasonKey}`)}</ActionLabel>
                  {a.count > 0 && <ActionCount>{a.count}</ActionCount>}
                </ActionRow>
              ))
            ) : (
              <EmptyActions>{t('common.noRecords')}</EmptyActions>
            )}
          </>
        )}
      </ReadinessCard>

      {hasHaModule && (
        <SmartStatsGrid>
          <SmartStatsRow hasHaModule={hasHaModule} />
        </SmartStatsGrid>
      )}

      <DataTable
        title={t('hrAnalytics.metricCatalog')}
        serverSide={false}
        fetching={fetching}
        columns={columns}
        data={tableRows}
        pageSize={10}
        onRowClick={openMetricDetail}
      />
    </Container>
  );
}
